/* MissingAcrossDealsTab.jsx — Documents Hub "Across deals" surface.
 *
 * This is the operational control centre, not a file explorer.
 *
 * What it does:
 *   • Fetches GET /api/documents/missing-across-deals (org-scoped flat list).
 *   • Renders a scannable, mobile-first table with severity pills,
 *     responsible-party labels, days-outstanding, exchange proximity
 *     and last-chased timestamps.
 *   • Multi-select rows → "Request documents" CTA opens a right-side
 *     operational panel.
 *   • Panel calls /api/documents/bulk-request/preview to get the
 *     calm grouped recipients + auto-generated messages. The send CTA
 *     calls /api/documents/bulk-request/send which delivers via Resend,
 *     bumps `last_chased_at` and writes a `document_request` activity
 *     row to each affected deal's timeline.
 *
 * Discipline:
 *   • Does NOT change the global Sidebar, page header, typography, or
 *     spacing rules. Renders strictly inside the existing tab slot.
 *   • Calm, no exclamation marks, no emoji icons (only lucide).
 *   • Mobile-first: sticky bottom CTA when any row is selected; thumb-
 *     friendly 44px-tall row hit-targets; right panel becomes a full-
 *     sheet on screens narrower than 1024px.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle, AlertTriangle, ArrowRight, Building2, CalendarClock,
  Check, ChevronRight, Clock, Loader2, Mail, MessageCircle, Send,
  Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ─── Helpers ──────────────────────────────────────────────────────── */

const SEV_STYLES = {
  red: {
    pill: "bg-rose-50 text-rose-700 border-rose-200",
    bar:  "bg-rose-500",
    icon: AlertCircle,
    label: "Critical",
  },
  amber: {
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    bar:  "bg-amber-400",
    icon: AlertTriangle,
    label: "Watch",
  },
  neutral: {
    pill: "bg-slate-50 text-slate-600 border-slate-200",
    bar:  "bg-slate-300",
    icon: Clock,
    label: "Routine",
  },
};

const ROLE_BADGE = {
  estate_agent:      "bg-violet-50 text-violet-700 border-violet-200",
  seller:            "bg-emerald-50 text-emerald-700 border-emerald-200",
  buyer:             "bg-sky-50 text-sky-700 border-sky-200",
  seller_solicitor:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  buyer_solicitor:   "bg-indigo-50 text-indigo-700 border-indigo-200",
  mortgage_broker:   "bg-amber-50 text-amber-700 border-amber-200",
};

const _fmtExchange = (iso, inDays) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  const datePart = isNaN(dt) ? iso : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  if (inDays === null || inDays === undefined) return datePart;
  if (inDays < 0)  return `${datePart} · ${Math.abs(inDays)}d ago`;
  if (inDays === 0) return `${datePart} · today`;
  return `${datePart} · in ${inDays}d`;
};

const _fmtLastChased = (iso) => {
  if (!iso) return "Never chased";
  try {
    const dt = new Date(iso);
    const ms = Date.now() - dt.getTime();
    const days = Math.floor(ms / 86_400_000);
    if (days <= 0) return "Chased today";
    if (days === 1) return "Chased yesterday";
    if (days < 30) return `Chased ${days}d ago`;
    return `Chased ${dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`;
  } catch { return "Never chased"; }
};

/* ─── Row primitive ───────────────────────────────────────────────── */

function MissingRow({ row, checked, onToggle }) {
  const sev = SEV_STYLES[row.severity] || SEV_STYLES.neutral;
  const SevIcon = sev.icon;
  const roleClass = ROLE_BADGE[row.responsible_role] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <label
      htmlFor={`missing-row-${row.deal_id}-${row.key}`}
      className={`group flex items-stretch gap-3 px-3.5 py-3 md:py-3.5 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors ${checked ? "bg-violet-50/40" : "hover:bg-slate-50/70"}`}
      data-testid={`bulk-missing-row`}
    >
      {/* Severity bar — calm 3px stripe on the left */}
      <span aria-hidden className={`w-[3px] rounded-full shrink-0 ${sev.bar}`} />

      {/* Checkbox — 24×24 thumb-friendly */}
      <input
        id={`missing-row-${row.deal_id}-${row.key}`}
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(row)}
        className="mt-1 h-[18px] w-[18px] shrink-0 rounded border-slate-300 accent-violet-600"
        data-testid={`bulk-missing-checkbox`}
      />

      {/* Body: label · property · meta row */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-slate-900 leading-tight">{row.label}</span>
          <span
            className={`text-[10px] uppercase tracking-[0.12em] font-bold px-1.5 py-[2px] rounded border ${sev.pill} inline-flex items-center gap-1`}
            title={`Severity: ${sev.label}`}
          >
            <SevIcon size={9} strokeWidth={2.6} /> {sev.label}
          </span>
        </div>
        <div className="mt-0.5 text-[12px] text-slate-600 flex items-center gap-1.5">
          <Building2 size={11} className="text-slate-400" strokeWidth={2.2} />
          <span className="truncate">{row.property}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
          <span className={`px-1.5 py-[1px] rounded border text-[10.5px] font-semibold ${roleClass}`}>
            {row.role_label}
          </span>
          <span className="inline-flex items-center gap-1">
            <CalendarClock size={10} className="text-slate-400" strokeWidth={2.2} />
            Exchange {_fmtExchange(row.target_exchange, row.target_exchange_in)}
          </span>
          <span>·</span>
          <span>{_fmtLastChased(row.last_chased_at)}</span>
          {row.days_outstanding > 0 && (
            <>
              <span>·</span>
              <span>{row.days_outstanding}d outstanding</span>
            </>
          )}
        </div>
      </div>
    </label>
  );
}

/* ─── Right-side bulk request panel ───────────────────────────────── */

function BulkRequestPanel({ open, onClose, selections, onSent }) {
  const [loading, setLoading]   = useState(false);
  const [sending, setSending]   = useState(false);
  const [note, setNote]         = useState("");
  const [preview, setPreview]   = useState(null);
  const [err, setErr]           = useState(null);

  // Refetch preview whenever the selection or note changes. Calm
  // debounce so the textarea doesn't spam the backend.
  useEffect(() => {
    if (!open || selections.length === 0) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/api/documents/bulk-request/preview", {
          selections: selections.map((s) => ({ deal_id: s.deal_id, key: s.key })),
          note: note?.trim() || undefined,
        });
        if (!cancelled) setPreview(r?.data || null);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail?.message || e?.message || "Could not generate preview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, selections, note]);

  const doSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post("/api/documents/bulk-request/send", {
        selections: selections.map((s) => ({ deal_id: s.deal_id, key: s.key })),
        note: note?.trim() || undefined,
      }, { timeout: 60_000 });
      const summary = r?.data?.summary || {};
      const sent = summary.sent_count || 0;
      const unresolved = summary.unresolved_count || 0;
      const totalItems = summary.item_count || 0;
      if (sent > 0) {
        const parts = [`${sent} chase${sent === 1 ? "" : "s"} sent`];
        if (totalItems) parts.push(`${totalItems} document${totalItems === 1 ? "" : "s"} requested`);
        if (unresolved) parts.push(`${unresolved} need a recorded contact`);
        toast.success(parts.join(" · "));
      } else if (unresolved > 0) {
        toast(`${unresolved} request${unresolved === 1 ? "" : "s"} need a recorded contact`, {
          description: "We didn't have an email for the responsible party. Add one to send the chase.",
        });
      } else {
        toast("Nothing to send", { description: "All selected items were already complete." });
      }
      onSent?.(r?.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.message || "Could not send chases";
      toast.error("Couldn't send the chases", { description: msg });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="bulk-request-panel">
      {/* Backdrop — hidden on mobile (the sheet is full-width) */}
      <button
        type="button"
        aria-label="Close panel"
        className="hidden md:block flex-1 bg-slate-900/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      {/* Panel — full sheet on mobile, 480px drawer on >=md */}
      <aside
        className="w-full md:max-w-[480px] md:ml-auto bg-white md:border-l border-slate-200 shadow-2xl flex flex-col overflow-hidden"
        role="dialog" aria-label="Bulk request documents"
      >
        {/* Header */}
        <header className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-bold tracking-[0.18em] uppercase text-violet-700">
              Bulk request
            </p>
            <h3 className="mt-1 text-[15px] font-semibold text-slate-900 leading-tight">
              Request documents from {preview?.summary?.recipient_count || selections.length} {(preview?.summary?.recipient_count || selections.length) === 1 ? "recipient" : "recipients"}
            </h3>
            {preview?.summary && (
              <p className="text-[11.5px] text-slate-500 mt-0.5">
                {preview.summary.item_count} item{preview.summary.item_count === 1 ? "" : "s"} ·
                {" "}{preview.summary.deal_count} transaction{preview.summary.deal_count === 1 ? "" : "s"}
                {preview.summary.unresolved_count > 0 && (
                  <span className="text-amber-700"> · {preview.summary.unresolved_count} unresolved</span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="bulk-request-panel-close"
          >
            <X size={15} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && !preview && (
            <div className="flex items-center gap-2 text-[12px] text-slate-500">
              <Loader2 size={12} className="animate-spin" /> Preparing your request preview…
            </div>
          )}
          {err && (
            <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          {/* Note input */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5">
              Note to recipients (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={600}
              placeholder="Anything time-sensitive worth flagging…"
              className="w-full text-[12.5px] leading-relaxed text-slate-800 px-3 py-2 rounded-lg border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none resize-none"
              data-testid="bulk-request-note"
            />
          </div>

          {/* Grouped recipients */}
          {preview?.groups?.length > 0 && (
            <div className="space-y-3">
              <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500">
                Grouped recipients
              </p>
              {preview.groups.map((g, idx) => {
                const sev = SEV_STYLES[g.severity] || SEV_STYLES.neutral;
                return (
                  <article
                    key={`${g.recipient_email || "unresolved"}-${idx}`}
                    className={`rounded-xl border p-3.5 ${g.is_unresolved ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"}`}
                    data-testid="bulk-request-group"
                  >
                    <header className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-slate-900 leading-tight truncate">
                          {g.recipient_name || g.role_label || "Counterparty"}
                          {g.recipient_organisation && (
                            <span className="ml-1 text-[11.5px] text-slate-500 font-normal">· {g.recipient_organisation}</span>
                          )}
                        </p>
                        <p className="text-[11.5px] text-slate-500 mt-0.5 truncate">
                          {g.is_unresolved ? (
                            <span className="text-amber-700">No contact recorded · please add an email to send</span>
                          ) : (
                            <>
                              <Mail size={10} className="inline -mt-[2px] mr-1 text-slate-400" strokeWidth={2.4} />
                              {g.recipient_email}
                            </>
                          )}
                        </p>
                      </div>
                      <span className={`shrink-0 text-[10px] uppercase tracking-[0.12em] font-bold px-1.5 py-[2px] rounded border ${sev.pill}`}>
                        {sev.label}
                      </span>
                    </header>
                    <p className="mt-2 text-[11.5px] text-slate-500">
                      {g.item_count} item{g.item_count === 1 ? "" : "s"} · across {g.deal_count} transaction{g.deal_count === 1 ? "" : "s"}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {g.items.map((it) => (
                        <li key={`${it.deal_id}-${it.key}`} className="text-[12px] text-slate-700 flex items-start gap-1.5">
                          <ChevronRight size={11} className="mt-[3px] text-slate-300 shrink-0" strokeWidth={2.2} />
                          <span className="flex-1 truncate">
                            <strong className="font-semibold text-slate-900">{it.label}</strong>
                            <span className="text-slate-500"> — {it.property}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                    {/* Preview of the actual message */}
                    {!g.is_unresolved && (
                      <details className="mt-3 group">
                        <summary className="text-[11.5px] font-semibold text-violet-700 cursor-pointer select-none inline-flex items-center gap-1">
                          <Sparkles size={10} strokeWidth={2.4} />
                          Preview message
                        </summary>
                        <div className="mt-2 px-3 py-2.5 bg-slate-50 rounded-lg text-[12px] text-slate-700 whitespace-pre-line leading-relaxed">
                          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-1.5">Subject</p>
                          <p className="text-[12.5px] font-medium text-slate-900 mb-2">{g.subject}</p>
                          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-1.5">Body</p>
                          {g.message_text}
                        </div>
                      </details>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer / send action */}
        <footer className="px-5 py-3.5 border-t border-slate-100 bg-white">
          <button
            type="button"
            onClick={doSend}
            disabled={sending || loading || !preview || (preview?.summary?.recipient_count === 0)}
            className="w-full h-11 rounded-xl text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#8B5CF6,#6366F1)" }}
            data-testid="bulk-request-send"
          >
            {sending ? (
              <><Loader2 size={13} className="animate-spin" /> Sending chases…</>
            ) : (
              <><Send size={13} /> Send {preview?.summary?.recipient_count ? `to ${preview.summary.recipient_count} recipient${preview.summary.recipient_count === 1 ? "" : "s"}` : "request"}</>
            )}
          </button>
          {preview?.summary?.unresolved_count > 0 && (
            <p className="mt-2 text-[11px] text-amber-700 text-center">
              {preview.summary.unresolved_count} request{preview.summary.unresolved_count === 1 ? "" : "s"} won't send — no contact recorded for them.
            </p>
          )}
        </footer>
      </aside>
    </div>
  );
}

/* ─── Public component ────────────────────────────────────────────── */

export function MissingAcrossDealsTab() {
  const [loading, setLoading]     = useState(true);
  const [rows, setRows]           = useState([]);
  const [bySev, setBySev]         = useState({ red: 0, amber: 0, neutral: 0 });
  const [filter, setFilter]       = useState("all");           // all | red | amber | neutral
  const [selected, setSelected]   = useState(new Map());        // Map<rowKey, row>
  const [panelOpen, setPanelOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/documents/missing-across-deals");
      setRows(r?.data?.rows || []);
      setBySev(r?.data?.by_severity || { red: 0, amber: 0, neutral: 0 });
    } catch (e) {
      toast.error("Could not load missing documents", { description: e?.response?.data?.detail?.message || e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.severity === filter);
  }, [rows, filter]);

  const rowKey = (r) => `${r.deal_id}:${r.key}`;
  const toggle = (r) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k);
      else next.set(k, r);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Map());
  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of filtered) next.set(rowKey(r), r);
      return next;
    });
  };

  const selections = useMemo(() => Array.from(selected.values()), [selected]);

  return (
    <div className="space-y-4" data-testid="missing-across-deals-tab">
      {/* Summary strip — three calm severity counts */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { id: "red",     label: "Critical", count: bySev.red,     color: "text-rose-600",  bg: "bg-rose-50" },
          { id: "amber",   label: "Watch",    count: bySev.amber,   color: "text-amber-600", bg: "bg-amber-50" },
          { id: "neutral", label: "Routine",  count: bySev.neutral, color: "text-slate-700", bg: "bg-slate-50" },
        ].map((c) => {
          const active = filter === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setFilter(active ? "all" : c.id)}
              className={`relative text-left px-4 py-3 rounded-xl border transition-all ${active ? "border-violet-300 ring-2 ring-violet-100" : "border-slate-200 hover:border-slate-300"} ${c.bg}`}
              data-testid={`bulk-filter-${c.id}`}
              aria-pressed={active}
            >
              <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500">{c.label}</p>
              <p className={`text-[24px] font-bold tabular-nums mt-0.5 ${c.color}`}>{c.count}</p>
              <p className="text-[10.5px] text-slate-500 mt-0.5">
                {active ? "Showing only these" : "Click to filter"}
              </p>
            </button>
          );
        })}
      </div>

      {/* Filter / select-all bar */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1">
        <div className="flex items-center gap-2 text-[11.5px] text-slate-500">
          {filter !== "all" && (
            <button
              type="button"
              onClick={() => setFilter("all")}
              className="px-2 py-0.5 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Clear filter
            </button>
          )}
          <span>{filtered.length} item{filtered.length === 1 ? "" : "s"}</span>
          {selected.size > 0 && (
            <>
              <span>·</span>
              <span className="font-semibold text-violet-700">{selected.size} selected</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selectAllVisible}
            disabled={filtered.length === 0}
            className="text-[11.5px] font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-40"
            data-testid="bulk-select-all-visible"
          >
            Select all visible
          </button>
          {selected.size > 0 && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-900"
              data-testid="bulk-clear-selection"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Rows */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-[12.5px] text-slate-500 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 size={13} className="animate-spin" /> Reading your transactions…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Check size={20} className="mx-auto text-emerald-500 mb-2" strokeWidth={2.4} />
            <p className="text-[13px] font-semibold text-slate-900">No outstanding documents</p>
            <p className="text-[11.5px] text-slate-500 mt-1">
              {filter === "all"
                ? "Every transaction is fully supplied. Take a breath."
                : "Nothing in this severity band right now."}
            </p>
          </div>
        ) : (
          filtered.map((row) => (
            <MissingRow
              key={`${row.deal_id}:${row.key}`}
              row={row}
              checked={selected.has(`${row.deal_id}:${row.key}`)}
              onToggle={toggle}
            />
          ))
        )}
      </div>

      {/* Sticky bottom CTA — appears on every screen size as soon as the
          operator selects anything. Mobile-thumb-friendly height. */}
      {selected.size > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 px-4 py-3 border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
          data-testid="bulk-sticky-cta-bar"
        >
          <div className="max-w-[1480px] mx-auto md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={clearSelection}
              className="h-11 px-3 text-[12.5px] font-semibold text-slate-600 hover:text-slate-900"
            >
              Clear ({selected.size})
            </button>
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="h-11 px-5 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-110 transition-all"
              style={{ background: "linear-gradient(135deg,#8B5CF6,#6366F1)" }}
              data-testid="bulk-request-open-panel"
            >
              <MessageCircle size={13} /> Request documents
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      <BulkRequestPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        selections={selections}
        onSent={() => {
          clearSelection();
          fetchData();
        }}
      />
    </div>
  );
}

export default MissingAcrossDealsTab;
